<!-- Product states (PD §5): 3 skeleton cards (P/proto-states.js:130), the error panel, the gone-listing panel (:121). -->
@if (loading) {
    <agu-skeleton [Count]="3" />
} @else if (error) {
    <agu-state-panel
        Variant="error"
        [Icon]="ErrorCopy.Icon"
        [Title]="ErrorCopy.Title"
        [Body]="ErrorCopy.Body"
        PrimaryLabel="Try again"
        [SecondaryLabel]="failures.SupportHref ? 'Contact support' : null"
        [SecondaryHref]="failures.SupportHref"
        [Foot]="failures.Foot"
        [Note]="failures.Note"
        (Primary)="Retry()"
    />
} @else if (!grant) {
    <agu-state-panel [Icon]="GoneCopy.Icon" [Title]="GoneCopy.Title" [Body]="GoneCopy.Body" PrimaryLabel="See similar" SecondaryLabel="Back to Home" (Primary)="SeeSimilar.emit()" (Secondary)="BackToHome.emit()" />
} @else {
    <!-- The crumb trail (P/detail.html:888): "Back to {origin}" / this record. Hidden on a phone, where the app bar carries it (P/Member_Hub.html:493). -->
    <agu-crumb [Trail]="Crumb.Trail" (Navigate)="Crumb.Navigate($event)" />

    <!--
      R-F11: the mockup's plain document head — pills, then a 30px h1, then the
      14.5px meta line, all on the page ground. `detail.html` has no tinted
      band; the module accent reaches this view through the finder page host's
      `data-accent`.
    -->
    @if (IsRecommended || grant.isExpired) {
        <div class="ra-dv__pills">
            @if (IsRecommended) {
                <span class="ra-dv__rectag">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="ra-dv__rectag-star">
                        <path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4z" />
                    </svg>
                    Recommended
                </span>
            }
            @if (grant.isExpired) {
                <span class="ra-dv__newpill">Closed</span>
            }
        </div>
    }

    <h1 class="ra-dv__title">{{ grant.title }}</h1>
    @if (OfficialLine) {
        <p class="ra-dv__meta ra-dv__meta--official">{{ OfficialLine }}</p>
    }
    @if (MetaLine) {
        <p class="ra-dv__meta">{{ MetaLine }}</p>
    }

    <div class="ra-dv__facts ra-dv__facts--g4">
        <div class="ra-dv__fact">
            <div class="ra-dv__fact-k">Award</div>
            <div class="ra-dv__fact-v ra-dv__fact-v--sm">{{ AwardLine }}</div>
        </div>
        <div class="ra-dv__fact">
            <div class="ra-dv__fact-k">Deadline</div>
            <div class="ra-dv__fact-v ra-dv__fact-v--sm">{{ DeadlineLine }}</div>
        </div>
        <div class="ra-dv__fact">
            <div class="ra-dv__fact-k">Eligibility</div>
            <div class="ra-dv__fact-v ra-dv__fact-v--sm">{{ EligibilityLine }}</div>
        </div>
        <div class="ra-dv__fact">
            <div class="ra-dv__fact-k">Posted</div>
            <div class="ra-dv__fact-v ra-dv__fact-v--sm">{{ PostedLine }}</div>
        </div>
    </div>

    <section class="ra-dv__sec" aria-labelledby="ra-detail-about-h">
        <h2 class="ra-dv__body-h" id="ra-detail-about-h">About</h2>
        <div class="ra-dv__desc">
            @if (DescriptionParagraphs.length === 0) {
                <p>No description was provided by the funder.</p>
            }
            @for (paragraph of DescriptionParagraphs; track $index) {
                <p>{{ paragraph }}</p>
            }
        </div>
    </section>

    <!-- `Who can apply` (P/detail.html:1002-1003): the itemised eligibility, each line led by the 11px check in a 19px teal disc (:309-312). -->
    <section class="ra-dv__sec" aria-labelledby="ra-detail-elig-h">
        <h2 class="ra-dv__body-h" id="ra-detail-elig-h">Who can apply</h2>
        @if ((grant.eligibility ?? []).length > 0) {
            <ul class="ra-dv__eliglist">
                @for (line of grant.eligibility ?? []; track $index) {
                    <li>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                        {{ line }}
                    </li>
                }
            </ul>
        } @else {
            <div class="ra-dv__desc"><p>Eligibility is not itemised for this opportunity, see the funder announcement.</p></div>
        }
    </section>

    <!-- The primary action leads, ahead of Save (detail.html:474-476 — Back, Apply, Save; Back is this view's own pill above). Pin is live-only and follows Save. -->
    <div class="ra-dv__acts">
        @if (grant.url) {
            <a class="ra-dv__act ra-dv__act--primary" [href]="grant.url" target="_blank" rel="noopener"><span class="ra-dv__act-label">Apply on funder site</span> <span class="ra-dv__act-arrow" aria-hidden="true">↗</span></a>
        } @else {
            <button type="button" class="ra-dv__act ra-dv__act--primary" disabled aria-disabled="true"><span class="ra-dv__act-label">Apply on funder site</span> <span class="ra-dv__act-arrow" aria-hidden="true">↗</span></button>
        }
        <button type="button" class="ra-dv__act ra-dv__act--ghost" [attr.aria-pressed]="grant.itemState.saved" (click)="OnSaveToggle()">
            <agu-icon Name="saved" [Size]="15" />
            <span class="ra-dv__act-label">{{ grant.itemState.saved ? 'Saved' : 'Save' }}</span>
        </button>
        <button type="button" class="ra-dv__act ra-dv__act--ghost" [attr.aria-pressed]="grant.itemState.pinned" (click)="OnPinToggle()">
            <i class="fa-solid fa-thumbtack" aria-hidden="true"></i>
            <span class="ra-dv__act-label">{{ grant.itemState.pinned ? 'Pinned' : 'Pin' }}</span>
        </button>
    </div>
}
